// pages/admin/QRCodePage.tsx
/**
 * @description Responsive QR code generation and management page
 */
import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ChevronLeft,
  Download,
  Copy,
  Check,
  Printer,
  QrCode,
  Smartphone,
  Monitor,
  ExternalLink
} from 'lucide-react';
import QRCodeStyling from 'qr-code-styling';
import { AdminLayout } from '../../components/layout';
import { Button, Card, Input, Toggle } from '../../components/ui';
import { MOCK_BUILDING } from '../../lib/mockData';

// QR code configuration options
interface QRConfig {
  size: number;
  dotsColor: string;
  backgroundColor: string;
  includeImage: boolean;
  imageSize: number;
  cornerStyle: 'square' | 'dots' | 'rounded';
}

export function QRCodePage() {
  const { slug } = useParams<{ slug: string }>();
  const qrRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  const [qrCode, setQrCode] = useState<QRCodeStyling | null>(null);

  // TODO: Replace with actual building lookup
  const building = MOCK_BUILDING;

  // QR code URL
  const baseUrl = window.location.origin;
  const publicUrl = `${baseUrl}/b/${slug}`;

  // QR configuration state
  const [config, setConfig] = useState<QRConfig>({
    size: 300,
    dotsColor: '#2563eb',
    backgroundColor: '#ffffff',
    includeImage: false,
    imageSize: 0.4,
    cornerStyle: 'rounded'
  });

  // Initialize QR code
  useEffect(() => {
    const qr = new QRCodeStyling({
      width: config.size,
      height: config.size,
      data: publicUrl,
      dotsOptions: {
        color: config.dotsColor,
        type: config.cornerStyle === 'rounded' ? 'rounded' :
              config.cornerStyle === 'dots' ? 'dots' : 'square'
      },
      backgroundOptions: {
        color: config.backgroundColor
      },
      cornersSquareOptions: {
        type: config.cornerStyle === 'rounded' ? 'extra-rounded' :
              config.cornerStyle === 'dots' ? 'dot' : 'square'
      },
      cornersDotOptions: {
        type: config.cornerStyle === 'rounded' ? 'dot' : 'square'
      },
      imageOptions: {
        crossOrigin: 'anonymous',
        margin: 10
      }
    });

    setQrCode(qr);

    if (qrRef.current) {
      qrRef.current.innerHTML = '';
      qr.append(qrRef.current);
    }
  }, [config, publicUrl]);

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleDownload = async (format: 'png' | 'svg') => {
    if (qrCode) {
      qrCode.download({
        name: `${slug}-qrcode`,
        extension: format
      });
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow && qrRef.current) {
      const svg = qrRef.current.querySelector('canvas');
      if (svg) {
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>QR Code - ${building.name}</title>
            <style>
              body {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                min-height: 100vh;
                margin: 0;
                font-family: system-ui, -apple-system, sans-serif;
              }
              .container {
                text-align: center;
                padding: 2rem;
              }
              h1 {
                font-size: 1.5rem;
                margin-bottom: 0.5rem;
              }
              p {
                color: #666;
                margin-bottom: 2rem;
              }
              img {
                max-width: 300px;
              }
              .url {
                margin-top: 1rem;
                font-size: 0.875rem;
                color: #666;
              }
              @media print {
                body { padding: 0; }
              }
            </style>
          </head>
          <body>
            <div class="container">
              <h1>${building.name}</h1>
              <p>Scan to view available units</p>
              <img src="${svg.toDataURL()}" alt="QR Code" />
              <p class="url">${publicUrl}</p>
            </div>
          </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.print();
      }
    }
  };

  return (
    <AdminLayout>
      <div className="p-4 lg:p-8">
        {/* Breadcrumb */}
        <div className="mb-4 lg:mb-6">
          <Link
            to={`/admin/buildings/${slug}`}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <ChevronLeft size={16} className="mr-1" />
            Back to Building
          </Link>
        </div>

        {/* Page Header */}
        <div className="mb-6 lg:mb-8">
          <h1 className="text-xl lg:text-2xl font-semibold text-gray-900">QR Code</h1>
          <p className="text-sm lg:text-base text-gray-500 mt-1">{building.name}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-8">
          {/* QR Code Preview */}
          <Card className="p-4 lg:p-6">
            <h2 className="text-base lg:text-lg font-semibold text-gray-900 mb-4 lg:mb-6">Preview</h2>

            {/* QR Code Display */}
            <div className="flex justify-center mb-4 lg:mb-6">
              <div
                ref={qrRef}
                className="bg-white p-3 lg:p-4 rounded-xl shadow-sm border border-gray-100"
              />
            </div>

            {/* URL Display */}
            <div className="bg-gray-50 rounded-lg p-3 lg:p-4 mb-4 lg:mb-6">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                Public URL
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs lg:text-sm text-primary truncate">
                  {publicUrl}
                </code>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCopyUrl}
                  className="flex-shrink-0 p-2"
                >
                  {copied ? (
                    <Check size={16} className="text-green-500" />
                  ) : (
                    <Copy size={16} />
                  )}
                </Button>
                <a
                  href={publicUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="ghost" size="sm" className="flex-shrink-0 p-2">
                    <ExternalLink size={16} />
                  </Button>
                </a>
              </div>
            </div>

            {/* Download Buttons - Stack on small mobile */}
            <div className="grid grid-cols-3 gap-2 lg:gap-3">
              <Button
                variant="secondary"
                className="w-full text-xs lg:text-sm"
                onClick={() => handleDownload('png')}
              >
                <Download size={14} className="mr-1 lg:mr-2" />
                PNG
              </Button>
              <Button
                variant="secondary"
                className="w-full text-xs lg:text-sm"
                onClick={() => handleDownload('svg')}
              >
                <Download size={14} className="mr-1 lg:mr-2" />
                SVG
              </Button>
              <Button
                variant="secondary"
                className="w-full text-xs lg:text-sm"
                onClick={handlePrint}
              >
                <Printer size={14} className="mr-1 lg:mr-2" />
                Print
              </Button>
            </div>
          </Card>

          {/* Customization Options */}
          <Card className="p-4 lg:p-6">
            <h2 className="text-base lg:text-lg font-semibold text-gray-900 mb-4 lg:mb-6">
              Customize
            </h2>

            <div className="space-y-5 lg:space-y-6">
              {/* Size */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Size
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {[200, 300, 400, 500].map((size) => (
                    <button
                      key={size}
                      onClick={() => setConfig((prev) => ({ ...prev, size }))}
                      className={`py-2 px-2 lg:px-3 text-xs lg:text-sm rounded-lg border transition-colors ${
                        config.size === size
                          ? 'border-primary bg-primary/5 text-primary'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {size}px
                    </button>
                  ))}
                </div>
              </div>

              {/* Colors */}
              <div className="grid grid-cols-2 gap-3 lg:gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    QR Color
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={config.dotsColor}
                      onChange={(e) =>
                        setConfig((prev) => ({
                          ...prev,
                          dotsColor: e.target.value
                        }))
                      }
                      className="w-10 h-10 rounded border border-gray-200 cursor-pointer"
                    />
                    <Input
                      value={config.dotsColor}
                      onChange={(e) =>
                        setConfig((prev) => ({
                          ...prev,
                          dotsColor: e.target.value
                        }))
                      }
                      className="flex-1 text-xs lg:text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Background
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={config.backgroundColor}
                      onChange={(e) =>
                        setConfig((prev) => ({
                          ...prev,
                          backgroundColor: e.target.value
                        }))
                      }
                      className="w-10 h-10 rounded border border-gray-200 cursor-pointer"
                    />
                    <Input
                      value={config.backgroundColor}
                      onChange={(e) =>
                        setConfig((prev) => ({
                          ...prev,
                          backgroundColor: e.target.value
                        }))
                      }
                      className="flex-1 text-xs lg:text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Corner Style */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Style
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {([
                    { value: 'square', label: 'Square' },
                    { value: 'rounded', label: 'Rounded' },
                    { value: 'dots', label: 'Dots' }
                  ] as const).map((style) => (
                    <button
                      key={style.value}
                      onClick={() =>
                        setConfig((prev) => ({
                          ...prev,
                          cornerStyle: style.value
                        }))
                      }
                      className={`py-2 px-2 lg:px-3 text-xs lg:text-sm rounded-lg border transition-colors ${
                        config.cornerStyle === style.value
                          ? 'border-primary bg-primary/5 text-primary'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {style.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Logo Toggle */}
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="font-medium text-gray-900 text-sm lg:text-base">Include Logo</p>
                  <p className="text-xs lg:text-sm text-gray-500">
                    Add your building logo to the center
                  </p>
                </div>
                <Toggle
                  checked={config.includeImage}
                  onChange={(checked) =>
                    setConfig((prev) => ({ ...prev, includeImage: checked }))
                  }
                />
              </div>
            </div>
          </Card>

          {/* Usage Guidelines - Full width on mobile */}
          <Card className="p-4 lg:p-6 lg:col-span-2">
            <h2 className="text-base lg:text-lg font-semibold text-gray-900 mb-4">
              Usage Guidelines
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
              <div className="flex gap-3 lg:gap-4">
                <div className="w-9 h-9 lg:w-10 lg:h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <QrCode size={18} className="text-blue-600" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 mb-1 text-sm lg:text-base">Print Size</h3>
                  <p className="text-xs lg:text-sm text-gray-600">
                    Minimum 2cm x 2cm for reliable scanning. Recommended 5cm x 5cm
                    for outdoor signage.
                  </p>
                </div>
              </div>
              <div className="flex gap-3 lg:gap-4">
                <div className="w-9 h-9 lg:w-10 lg:h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                  <Smartphone size={18} className="text-green-600" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 mb-1 text-sm lg:text-base">Placement</h3>
                  <p className="text-xs lg:text-sm text-gray-600">
                    Position at eye level (1.5m height) near building entrance
                    for easy scanning.
                  </p>
                </div>
              </div>
              <div className="flex gap-3 lg:gap-4">
                <div className="w-9 h-9 lg:w-10 lg:h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <Monitor size={18} className="text-purple-600" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 mb-1 text-sm lg:text-base">Testing</h3>
                  <p className="text-xs lg:text-sm text-gray-600">
                    Always test printed QR codes with multiple devices before
                    final installation.
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
